import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Brain, Repeat, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtHour = (h) => `${(h % 12) || 12}${h < 12 ? 'am' : 'pm'}`;

const LearningInsights = () => {
  const { auth } = useContext(AuthContext);
  const [win, setWin] = useState('month');
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    axios.get(`${API}/learning/profile?window=${win}&tz_offset_minutes=${-new Date().getTimezoneOffset()}`,
      { headers: { Authorization: `Bearer ${auth.token}` } })
      .then(r => setData(r.data)).catch(() => setData({ error: true }));
  }, [win, auth.token]);

  return (
    <div className="glass-card p-6 mb-8" data-testid="learning-insights-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Brain size={16} className="text-violet-300" />
          <h2 className="text-sm font-semibold text-white">What SpaceCal has learned</h2>
        </div>
        <div className="flex gap-1 p-1 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)' }}>
          {['week', 'month'].map(w => (
            <button key={w} onClick={() => setWin(w)} data-testid={`learning-window-${w}-btn`}
              className={`px-3 py-1 rounded-md text-xs capitalize transition-colors ${win === w ? 'text-violet-200 bg-violet-400/15' : 'text-gray-500 hover:text-gray-300'}`}>
              Past {w}
            </button>
          ))}
        </div>
      </div>
      {!data ? <Loader2 size={18} className="text-violet-300 animate-spin" /> : data.error ? (
        <p className="text-xs text-red-300">Could not load learning profile.</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-4 mb-4 text-xs text-gray-400">
            <span data-testid="learning-stage">Stage: <b className="text-white">{data.stage}</b></span>
            <span data-testid="learning-data-points">{data.data_points} data points</span>
            <span>Confidence {Math.round(data.confidence * 100)}%</span>
            {data.peak_hours?.length > 0 && <span>Peak: {data.peak_hours.map(fmtHour).join(', ')}</span>}
          </div>
          <div className="h-1.5 rounded-full bg-white/5 mb-4 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-violet-400 to-cyan-400 transition-[width] duration-700" style={{ width: `${Math.round(data.confidence * 100)}%` }} />
          </div>
          <ul className="space-y-1.5 mb-4" data-testid="learning-insights-list">
            {data.insights.map((t, i) => <li key={i} className="text-sm text-gray-300">• {t}</li>)}
          </ul>
          {data.routines?.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="learning-routines">
              {data.routines.slice(0, 6).map((r, i) => (
                <span key={i} className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full text-cyan-200"
                  style={{ background: 'rgba(0,217,255,0.08)', border: '1px solid rgba(0,217,255,0.2)' }}>
                  <Repeat size={11} /> {r.title} · {r.days.join('/')} {r.time}
                </span>
              ))}
            </div>
          )}
          <p className="text-[11px] text-gray-500 mt-4">New tasks are placed using these patterns — completing, moving or cancelling tasks keeps teaching it.</p>
        </>
      )}
    </div>
  );
};

export default LearningInsights;
